import Layout from '../../components/Layout';
import GuideCard from '../../components/GuideCard';
import { Link } from 'react-router-dom';
import { ArrowRight, Droplets, Moon, Sun, Sparkles, Scale } from 'lucide-react';

const skincareGuides = [
  {
    title: "Morning Skincare Routine",
    subtitle: "A simple, effective morning routine to protect and prepare your skin for the day.",
    category: "Routine",
    link: "/skincare/morning-routines",
    imageUrl: "https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/2lh9egs8_morning-skincare.jpg.png"
  },
  {
    title: "Evening Skincare Routine",
    subtitle: "Wind down with a nourishing evening routine that repairs and renews.",
    category: "Routine",
    link: "/skincare/evening-routines",
    imageUrl: "https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/dgbjbydn_evening-skincare.jpg.png"
  },
  {
    title: "Acne-Prone Skin Guide",
    subtitle: "Gentle, effective products and routines for managing breakouts and post-inflammatory marks.",
    category: "Concern",
    link: "/skincare/acne",
    imageUrl: "https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/wn47wunn_acne-prone-skin.jpg.png"
  },
  {
    title: "Dark Spots & Hyperpigmentation",
    subtitle: "Brighten and even out your skin tone with targeted recommendations for all skin tones.",
    category: "Concern",
    link: "/skincare/dark-spots-hyperpigmentation",
    imageUrl: "https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/qalmygj9_hyperpigmentation.jpg.png"
  },
  {
    title: "Sensitive Skin Guide",
    subtitle: "Soothe and protect reactive skin with gentle, fragrance-free picks.",
    category: "Skin Type",
    link: "/skincare/sensitive-skin",
    imageUrl: "https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/8n6avok8_sensitive-skin.jpg.png"
  }
];

const browseTopics = [
  { icon: Sun, label: "Morning Routines", link: "/skincare/morning-routines" },
  { icon: Moon, label: "Evening Routines", link: "/skincare/evening-routines" },
  { icon: Droplets, label: "Acne", link: "/skincare/acne" },
  { icon: Sparkles, label: "Dark Spots", link: "/skincare/dark-spots-hyperpigmentation" },
  { icon: Droplets, label: "Sensitive Skin", link: "/skincare/sensitive-skin" },
  { icon: Scale, label: "Compare Products", link: "/skincare/compare" },
];

const SkincareLanding = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="skincare-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
              Skincare
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1C1C1C] leading-tight mb-6">
              Build a routine that <span className="italic font-normal">actually works</span>
            </h1>
            <p className="text-base md:text-lg text-[#374151] leading-relaxed">
              Whether you're just starting out or refining your routine, our skincare guides help you choose the right products for your unique skin needs—without the overwhelm.
            </p>
          </div>
        </div>
      </section>

      {/* Start Here Section */}
      <section data-testid="skincare-start-here" className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="p-8 md:p-12 bg-[#F5F5F0] border border-[#E5E5E5]">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <span className="text-xs tracking-widest uppercase font-medium text-[#3A4D39] mb-2 block">New to Skincare?</span>
                <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-2">
                  Start with the basics
                </h3>
                <p className="text-[#374151] max-w-xl">
                  Our morning routine guide covers the essential steps everyone should know. It's the perfect starting point for beginners.
                </p>
              </div>
              <Link 
                to="/skincare/morning-routines"
                data-testid="skincare-start-cta"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#3A4D39] text-white text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#2A3D29] flex-shrink-0"
              >
                Start Here
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Browse by Topic */}
      <section data-testid="skincare-browse-topics" className="py-16 md:py-20 bg-[#FDFCF8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-8">
            Browse by Topic
          </h2>
          <div className="flex flex-wrap gap-3">
            {browseTopics.map((topic) => (
              <Link
                key={topic.label}
                to={topic.link}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-[#E5E5E5] text-[#374151] text-sm font-medium transition-all duration-300 hover:border-[#3A4D39] hover:text-[#3A4D39]"
              >
                <topic.icon className="w-4 h-4" />
                {topic.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* All Guides */}
      <section data-testid="skincare-all-guides" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1C1C1C] mb-4">
              All Skincare Guides
            </h2>
            <p className="text-[#374151]">
              Explore all our skincare guides, from daily routines to targeted treatments.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {skincareGuides.map((guide) => (
              <GuideCard key={guide.title} {...guide} />
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SkincareLanding;
